import { FC } from 'react';
import { useDrag } from 'react-dnd';
import { Box, Card, Tooltip, Typography } from '@mui/material';
import { ReactComponent as ReorderIcon } from 'src/assets/reorder.svg';
import { palette } from 'src/Theme';
import DateTimeUtils from 'src/utils/datetime';
import { DragItem, DragAndDropZone } from 'src/constants/drag-and-drop';
import { DragAndDropData } from '../interfaces/DragAndDrop.interface';
import { ReactComponent as UsersIcon } from 'src/assets/users.svg';
import { UnknownAvatar } from 'src/components/common';
import { ConsultationType, EducationLevel, EducationLevelGroup, EducationLevelGroupList } from 'src/constants';
import { hashCode } from 'src/utils';
import { ScheduleCalendarEvent } from '../../List/ScheduleCalendar/ScheduleCalendar.types';
import styled from '@emotion/styled';
import { ScheduleManager } from 'src/managers/Schedule/ScheduleManager';

const preschoolLabels = EducationLevelGroupList[EducationLevelGroup.PRESCHOOL].map(({ id }) => id);
const schoolLabels = [
    ...EducationLevelGroupList[EducationLevelGroup.JUNIOR_SCHOOL].map(({ id }) => id),
    ...EducationLevelGroupList[EducationLevelGroup.HIGH_SCHOOL].map(({ id }) => id)
];

function getChildLabel(count?: number): string {
    if (!count) {
        return '-';
    }
    const lastDigit = count % 10;

    if (lastDigit === 1) return `${count} учень`;
    if (lastDigit >= 2 && lastDigit <= 4) return `${count} учня`;

    return `${count} учнів`;
}

const StyledCard = styled(Card, { shouldForwardProp: (propName: string) => !propName.startsWith('$') })<{
    $enabled?: boolean;
    $title?: string;
}>(({ $enabled, $title }) => ({
    display: 'flex',
    gap: '8px',
    backgroundColor: $enabled
        ? palette.lessonColors[Math.abs(hashCode($title?.toString() || '') % palette.lessonColors.length)]
        : '#9B9FA2',
    padding: '4px 8px',
    cursor: $enabled ? 'pointer' : 'not-allowed',
    height: '100%',
    userSelect: 'none',
    overflow: 'auto'
}));

const StyledBox = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'start',
    minWidth: 0,
    borderRadius: '8px',
    color: palette.primary.creamyWhite,
    fontSize: '10px',
    width: '100%'
});

const StyledLabel = styled(Typography)({
    fontSize: '11px',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis'
});

const StyledHeadLabel = styled(StyledLabel)({
    fontWeight: 600,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis'
});

const styleMonthCard = {
    color: 'white',
    fontSize: '12px',
    padding: '2px',
    display: 'flex',
    gap: '4px'
};

const StyledMonthCard = styled(Card, { shouldForwardProp: (propName: string) => !propName.startsWith('$') })<{
    $title?: string;
}>(({ $title }) => ({
    backgroundColor: palette.lessonColors[Math.abs(hashCode($title?.toString() || '') % palette.lessonColors.length)],
    ...styleMonthCard
}));

function getEducationLevelLabel(educationLevels: EducationLevel[]): string {
    let label: string[] = [];

    const preschoolEducation = educationLevels.filter((level) => preschoolLabels.includes(level));
    const schoolEducation = educationLevels.filter((level) => schoolLabels.includes(level));

    if (preschoolEducation.length > 0) {
        label.push('дошкільна освіта');
    }

    if (schoolEducation.length > 0) {
        const uniqueGrades = Array.from(new Set(schoolEducation)).sort((a, b) => {
            const aNumber = parseInt(a.substring('GRADE'.length));
            const bNumber = parseInt(b.substring('GRADE'.length));

            return aNumber - bNumber;
        });

        const labels = uniqueGrades.map((grade) => {
            const number = parseInt(grade.substring('GRADE'.length));
            return number.toString();
        });

        label.push(`клас${labels.length > 1 ? 'и' : ''} ${labels.join(',')}`);
    }

    return label.join(', ');
}

export const ScheduleEventCard: FC<{
    event: ScheduleCalendarEvent;
    enabled?: boolean;
    dragIcon?: boolean;
    allowDelete?: boolean;
    onDoubleClick?: (event: ScheduleCalendarEvent) => void;
}> = ({ event, dragIcon, onDoubleClick, enabled = true, allowDelete = false }) => {
    const {
        title,
        start,
        end,
        educationLevels,
        children,
        employee,
        isEventInSameTimeSlot,
        consultationType,
        durationMinutes
    } = event;

    // todo: use dragPreviewRef to show rotated preview
    const [{ isDragging }, dragRef, dragPreviewRef] = useDrag(
        () => ({
            type: DragItem.SCHEDULE_CARD,
            item: { ...event },
            collect: (monitor) => ({
                isDragging: monitor.isDragging()
            })
        }),
        [event]
    );

    const EventCardContentComponent = ({ isEventCardContent = true }) => {
        const editInfoElement = <>{ScheduleManager.isCalendarEventEdit() ? 'Натисніть двічі, щоб редагувати' : ''}</>;
        if (!isEventCardContent && !isEventInSameTimeSlot) {
            return editInfoElement;
        }
        const renderTitle = consultationType
            ? title + ' (' + ConsultationType[consultationType as keyof typeof ConsultationType] + ')'
            : title;
        const titleComponent = isEventCardContent ? (
            <StyledHeadLabel sx={{ width: '100%' }}>{renderTitle}</StyledHeadLabel>
        ) : (
            renderTitle
        );

        let timeInfoElement = null;
        if (start && end) {
            const timeInfo = DateTimeUtils.getTime(start) + '-' + DateTimeUtils.getTime(end);
            timeInfoElement = isEventCardContent ? <StyledLabel>{timeInfo}</StyledLabel> : <div>{timeInfo}</div>;
        } else if (dragIcon && durationMinutes) {
            timeInfoElement = <div>{durationMinutes} хв</div>;
        }

        return (
            <Box sx={{ pointerEvents: 'none', width: '100%' }}>
                {titleComponent}
                {timeInfoElement}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
                    <StyledLabel
                        sx={{
                            flexShrink: 1,
                            fontSize: '10px'
                        }}>
                        {getEducationLevelLabel(educationLevels || []) || '-'}
                    </StyledLabel>
                    <span style={{ display: 'flex', gap: '2px', flexShrink: 0, alignItems: 'center' }}>
                        <UsersIcon /> {getChildLabel(children?.length)}
                    </span>
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        gap: '2px',
                        alignItems: 'center',
                        justifyContent: 'start',
                        marginTop: '2px'
                    }}>
                    {employee && (
                        <>
                            <UnknownAvatar firstName={employee.name} lastName={employee.surname} size="extra-small" />
                            <Typography variant="inherit" whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">
                                {employee.name} {employee.surname}
                            </Typography>
                        </>
                    )}
                    {!employee && <Typography variant="inherit">-</Typography>}
                </Box>
                {!isEventCardContent && editInfoElement}
            </Box>
        );
    };

    const isTooltipEventCardAvailable = ScheduleManager.isTooltipEventCardAvailable(
        false,
        isEventInSameTimeSlot ?? false
    );
    return (
        <StyledCard
            ref={enabled && ScheduleManager.isCalendarEventEdit() ? dragRef : null}
            $title={title}
            title=""
            $enabled={enabled}
            onDoubleClick={onDoubleClick ? () => onDoubleClick(event) : () => {}}>
            {dragIcon && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ReorderIcon />
                </Box>
            )}
            {isTooltipEventCardAvailable && (
                <Tooltip title={!dragIcon && <EventCardContentComponent isEventCardContent={false} />} arrow>
                    <StyledBox>
                        <EventCardContentComponent />
                    </StyledBox>
                </Tooltip>
            )}
            {!isTooltipEventCardAvailable && (
                <StyledBox>
                    <EventCardContentComponent />
                </StyledBox>
            )}
        </StyledCard>
    );
};

//TODO: revrite event according to design
export const ScheduleMonthEventCard: FC<{
    event: ScheduleCalendarEvent;
}> = ({ event }) => {
    const EventCardContentComponent = () => {
        return (
            <>
                {event.start && DateTimeUtils.getTime(event.start)}-{event.end && DateTimeUtils.getTime(event.end)}
                <Typography
                    fontWeight="600"
                    fontSize="inherit"
                    whiteSpace="nowrap"
                    overflow="hidden"
                    textOverflow="ellipsis">
                    {event.title}
                </Typography>
            </>
        );
    };
    return (
        <StyledMonthCard $title={event.title} title="">
            <Tooltip title={<EventCardContentComponent />} sx={{ ...styleMonthCard, backgroundColor: 'inherit' }} arrow>
                <Box>
                    <EventCardContentComponent />
                </Box>
            </Tooltip>
        </StyledMonthCard>
    );
};
